import { api } from "./api";
import type { BatchPlan, FeedProduction } from "@/types/models";

interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

// What produce/update resolve to. The axios interceptor unwraps the backend's
// {success, data, warnings} envelope; warnings only travel when there are any,
// so a clean batch arrives as the bare row and a caveated one as
// {data, warnings}. Normalised here so pages see one shape.
interface ProduceResponse {
  data: FeedProduction | null;
  warnings: string[];
}

const normaliseProduceResponse = (body: unknown): ProduceResponse => {
  if (body && typeof body === "object" && "warnings" in body) {
    const b = body as { data?: FeedProduction | null; warnings?: string[] };
    return { data: b.data ?? null, warnings: b.warnings ?? [] };
  }
  return { data: (body as FeedProduction | null) ?? null, warnings: [] };
};

export const feedProductionService = {
  preview: (formulaId: number, batchSizeKg: number) =>
    api
      .get<BatchPlan>("/feed-production/preview", { params: { formula_id: formulaId, batch_size_kg: batchSizeKg } })
      .then((r) => r.data),
  produce: (data: {
    formula_id: number;
    date: string;
    batch_size_kg: number;
    notes?: string;
  }) =>
    api
      .post<unknown>("/feed-production", data)
      .then((r) => normaliseProduceResponse(r.data)),
  list: (params?: Record<string, string | number>) =>
    api
      .get<PaginatedResponse<FeedProduction>>("/feed-production", { params })
      .then((r) => r.data),
  get: (id: number) =>
    api
      .get<FeedProduction>(`/feed-production/${id}`)
      .then((r) => r.data),
  update: (id: number, data: {
    formula_id: number;
    date: string;
    batch_size_kg: number;
    notes?: string;
  }) =>
    api
      .put<unknown>(`/feed-production/${id}`, data)
      .then((r) => normaliseProduceResponse(r.data)),
  delete: (id: number) =>
    api.delete(`/feed-production/${id}`).then((r) => r.data),
};
